import React, { useState, useEffect } from 'react';
import {
    Typography,
    Box,
    Chip,
    Divider,
    CircularProgress,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Avatar,
    Button
} from '@mui/material';
import {
    LocationOn,
    Phone,
    Email,
    Business,
    Person as PersonIcon,
    CheckCircle as ActiveIcon,
    Cancel as InactiveIcon
} from '@mui/icons-material';
import GenericModal from '../../components/GenericModal';
import { branchApi, Branch, BranchUser } from '../../services/branchApi';
import { useTheme } from '../../contexts/ThemeContext';

interface BranchViewProps {
    open: boolean;
    onClose: () => void;
    branchId: number | null;
}

const BranchView: React.FC<BranchViewProps> = ({ open, onClose, branchId }) => {
    const { theme } = useTheme();
    const [branch, setBranch] = useState<Branch | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (open && branchId) {
            loadBranch();
        }
        if (!open) {
            setBranch(null);
            setError(null);
        }
    }, [open, branchId]);

    const loadBranch = async () => {
        if (!branchId) return;

        setLoading(true);
        setError(null);
        try {
            const branchData = await branchApi.getBranch(branchId);
            setBranch(branchData);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load branch details');
        } finally {
            setLoading(false);
        }
    };

    const isStatusActive = (status: any): boolean => {
        if (typeof status === 'boolean') return status;
        if (typeof status === 'number') return status === 1;
        return status === 'active';
    };

    const getStatusColor = (status: any) => {
        return isStatusActive(status) ? 'success' : 'error';
    };

    const getStatusLabel = (status: any) => {
        return isStatusActive(status) ? 'Active' : 'Inactive';
    };

    const getTypeColor = (type: string) => {
        switch (type) {
            case 'Main Branch':
                return 'primary';
            case 'Express Center':
                return 'info';
            case 'Shop N Shop (SNS)':
                return 'secondary';
            case 'Franchise':
                return 'warning';
            default:
                return 'default';
        }
    };

    const getUserTypeColor = (type: string) => {
        switch (type) {
            case 'admin':
                return 'error';
            case 'manager':
                return 'primary';
            default:
                return 'default';
        }
    };

    const formatUserType = (type: string) => {
        return type.charAt(0).toUpperCase() + type.slice(1);
    };

    const renderContent = () => {
        if (loading) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                </Box>
            );
        }

        if (error) {
            return (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            );
        }

        if (!branch) {
            return null;
        }

        return (
            <Box>
                {/* Branch Header */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                    <Avatar
                        sx={{
                            bgcolor: theme.palette.primary.main,
                            width: 56,
                            height: 56
                        }}
                    >
                        <Business sx={{ fontSize: 28 }} />
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="h5" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                            {branch.name}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                            <Chip
                                label={branch.code}
                                color="primary"
                                variant="outlined"
                                size="small"
                            />
                            <Chip
                                label={branch.type || 'N/A'}
                                color={getTypeColor(branch.type) as any}
                                size="small"
                            />
                            <Chip
                                icon={isStatusActive(branch.status) ? <ActiveIcon /> : <InactiveIcon />}
                                label={getStatusLabel(branch.status)}
                                color={getStatusColor(branch.status)}
                                size="small"
                                variant="outlined"
                            />
                        </Box>
                    </Box>
                </Box>

                <Divider sx={{ my: 3 }} />

                {/* Branch Details */}
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: theme.palette.text.primary }}>
                    Branch Information
                </Typography>

                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                    gap: 3,
                    mb: 3
                }}>
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <LocationOn sx={{ fontSize: 18, color: theme.palette.text.secondary }} />
                            <Typography variant="caption" color="textSecondary">
                                City
                            </Typography>
                        </Box>
                        <Typography variant="body1" sx={{ ml: 3.5 }}>
                            {branch.city?.name || 'N/A'}
                        </Typography>
                    </Box>

                    <Box>
                        <Typography variant="caption" color="textSecondary">
                            Zone
                        </Typography>
                        <Typography variant="body1">
                            {branch.city?.zone || 'N/A'}
                        </Typography>
                    </Box>

                    {branch.created_at && (
                        <Box>
                            <Typography variant="caption" color="textSecondary">
                                Created At
                            </Typography>
                            <Typography variant="body1">
                                {new Date(branch.created_at).toLocaleDateString()}
                            </Typography>
                        </Box>
                    )}

                    {branch.updated_at && (
                        <Box>
                            <Typography variant="caption" color="textSecondary">
                                Last Updated
                            </Typography>
                            <Typography variant="body1">
                                {new Date(branch.updated_at).toLocaleDateString()}
                            </Typography>
                        </Box>
                    )}
                </Box>

                {/* Branch Users Section */}
                <Divider sx={{ my: 3 }} />

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <PersonIcon sx={{ color: theme.palette.primary.main }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                        Branch Staff
                    </Typography>
                    {branch.users && (
                        <Chip
                            label={branch.users.length}
                            size="small"
                            color="primary"
                            variant="outlined"
                        />
                    )}
                </Box>

                {branch.users && branch.users.length > 0 ? (
                    <TableContainer
                        component={Paper}
                        sx={{
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: 2
                        }}
                    >
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: theme.palette.action.hover }}>
                                    <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {branch.users.map((user: BranchUser) => (
                                    <TableRow key={user.id} hover>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Avatar
                                                    sx={{
                                                        width: 28,
                                                        height: 28,
                                                        fontSize: 12,
                                                        bgcolor: theme.palette.secondary.main
                                                    }}
                                                >
                                                    {user.name.charAt(0).toUpperCase()}
                                                </Avatar>
                                                {user.name}
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <Email sx={{ fontSize: 14, color: theme.palette.text.secondary }} />
                                                {user.email}
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={formatUserType(user.type)}
                                                color={getUserTypeColor(user.type) as any}
                                                size="small"
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                icon={isStatusActive(user.status) ? <ActiveIcon /> : <InactiveIcon />}
                                                label={getStatusLabel(user.status)}
                                                color={getStatusColor(user.status)}
                                                size="small"
                                                variant="outlined"
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                ) : (
                    <Box
                        sx={{
                            textAlign: 'center',
                            py: 3,
                            bgcolor: theme.palette.action.hover,
                            borderRadius: 2
                        }}
                    >
                        <PersonIcon sx={{ fontSize: 40, color: theme.palette.text.disabled, mb: 1 }} />
                        <Typography variant="body2" color="textSecondary">
                            No admin or managers assigned to this branch
                        </Typography>
                    </Box>
                )}
            </Box>
        );
    };

    return (
        <GenericModal
            open={open}
            onClose={onClose}
            title={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Business sx={{ color: theme.palette.primary.main }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Branch Details
                    </Typography>
                </Box>
            }
            maxWidth="md"
            actions={
                <Button
                    onClick={onClose}
                    variant="outlined"
                    sx={{
                        borderRadius: 2,
                        borderColor: theme.palette.divider,
                        color: theme.palette.text.secondary,
                        '&:hover': {
                            borderColor: theme.palette.text.secondary,
                        }
                    }}
                >
                    Close
                </Button>
            }
        >
            {renderContent()}
        </GenericModal>
    );
};

export default BranchView;
